import { useRef, useState, useEffect, useCallback } from 'react';

import axios, { endpoints } from 'src/utils/axios';
import { pauseAfterListing } from 'src/utils/marketplace-pace';
import { trackEvent, ANALYTICS_EVENTS } from 'src/utils/posthog';
import { isBlockedAsAutomation } from 'src/utils/platform-error-message';
import { paceWaitMs, priorCreateOutcome } from 'src/utils/extension-publish-rules';
import { buildExtensionPayload, publishOneExtensionListing } from 'src/utils/extension-publish';

import { EXTENSION_PLATFORMS } from '../constants';
import { createMarketplaceStops } from '../utils/marketplace-stops';
import { readCompletedWork, recordCompletedWork } from '../utils/extension-progress-log';
import { rememberAutomationRefusal, createAutomationRefusalTally } from '../utils/automation-block';

/**
 * Drives the Chrome extension through a list of listing creates, one at a time.
 *
 * Poshmark and Depop have no server-to-server create API, so the browser has to do the work:
 * for each (listing, platform) the runner claims the create on the server, asks the extension
 * to post it, and reports the outcome back. Two of these loops must never run at once — they
 * would create the same listing twice — so a run started for a new batch first stops the run
 * before it and waits for it to actually stop.
 *
 * Results carry the platform's RAW error text. Turning that into something a seller can read is
 * the rendering layer's job (src/utils/platform-error-message.js), so the same failure can be phrased
 * differently in different views.
 */

// How often a paused run re-checks whether it may continue. Pause is a user action, so a
// quarter-second of granularity is plenty.
const PAUSE_CHECK_MS = 250;

// The listing details response (GET /api/listings/details/:id) is built by the server's
// serializeListing, which derives these fields from the listing's platform placements: the
// per-platform post id, and the aspects blob carrying pendingSyncAction / pendingSyncClaimedAt.
// Reading them here is how the runner knows a create already happened or is in flight.
const PLATFORM_FIELDS = Object.fromEntries(
  EXTENSION_PLATFORMS.map((platform) => [
    platform,
    { listingId: `${platform}ListingId`, aspects: `${platform}Aspects` },
  ])
);

const emptyProgress = { current: 0, total: 0, currentListing: '', currentPlatform: '' };

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * @param {Object} options
 * @param {string} options.batchId - the batch this runner works for; namespaces the completed-work log
 * @param {(progress:{current:number,total:number,currentListing:string,currentPlatform:string}) => void} [options.onProgress]
 * @param {(listingId:string, platform:string, result:object) => void} [options.onResult]
 * @param {(platform:string) => void} [options.onAutomationBlock] - a marketplace refused this run as
 *   automation, so the rest of its items were left alone
 * @returns {{
 *   start: (pendingItems: Array<{listingId:string, listingTitle:string, platform:string}>) => Promise<void>,
 *   retry: (listingId:string, platform:string) => Promise<object>,
 *   cancel: () => void,
 *   pause: () => void,
 *   resume: () => void,
 *   phase: 'idle'|'processing'|'paused'|'done',
 *   isPaused: boolean,
 *   liveProgress: {current:number, total:number, currentListing:string, currentPlatform:string},
 * }}
 */
export function useExtensionRunner({ batchId, onProgress, onResult, onAutomationBlock } = {}) {
  const [phase, setPhase] = useState('idle');
  const [isPaused, setIsPaused] = useState(false);
  const [liveProgress, setLiveProgress] = useState(emptyProgress);

  // Every run gets its own control object: `{ batchId, cancelled, paused, finished, promise }`,
  // and the loop closes over its own control. That buys three guarantees:
  //   - pause and cancel reach a run that is already in flight;
  //   - a superseded run is stopped and waited for before its replacement starts, so only one
  //     run ever talks to the extension;
  //   - a run that outlives the batch on screen keeps writing its OWN completion log but stops
  //     updating the view, and only the run still holding the runner sets the shared phase.
  const controlRef = useRef(null);

  const batchIdRef = useRef(batchId);
  useEffect(() => {
    batchIdRef.current = batchId;
    // A different batch shows its own empty progress rather than the numbers left behind by the
    // batch before it — unless a run from that earlier batch is still creating, in which case
    // the phase has to keep saying so: something is talking to the extension right now, and
    // closing the widget must offer to stop it.
    if (controlRef.current && !controlRef.current.finished) return;
    setPhase('idle');
    setIsPaused(false);
    setLiveProgress(emptyProgress);
  }, [batchId]);

  // Callbacks are read through refs: the loop closure is created once per run and must still
  // reach the caller's latest handlers when it finishes minutes later.
  const onProgressRef = useRef(onProgress);
  const onResultRef = useRef(onResult);
  const onAutomationBlockRef = useRef(onAutomationBlock);
  useEffect(() => { onProgressRef.current = onProgress; }, [onProgress]);
  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  useEffect(() => { onAutomationBlockRef.current = onAutomationBlock; }, [onAutomationBlock]);

  // Results and progress belong to a batch, so only work done for the batch on screen may update
  // what the user is looking at.
  const steersView = useCallback((runBatchId) => runBatchId === batchIdRef.current, []);

  // The phase describes the one shared extension loop, so only the run still holding it may set
  // the phase — whichever batch that run belongs to.
  const holdsRunner = useCallback((control) => controlRef.current === control, []);

  // ── One platform create ───────────────────────────────────────────────────────────────────

  const reportResult = useCallback((runBatchId, listingId, platform, result) => {
    if (steersView(runBatchId)) onResultRef.current?.(listingId, platform, result);
  }, [steersView]);

  const publishResult = useCallback((runBatchId, listingId, platform, result) => {
    // Every attempt is written down, including one that ended unable to say whether the post was
    // made. This log is what stops a reload of the same batch attempting it again, and a create
    // whose post may be live is the single most important one not to attempt twice. It records
    // the outcome, not a verdict of success: replaying it shows the same "we could not tell"
    // row, and the run still reads as needing the seller.
    //
    // The pre-scan above reads the placement's marketplace id before this log, so a post that has
    // since been linked is reported as already listed rather than from a stale entry here.
    recordCompletedWork(runBatchId, listingId, platform, result);
    reportResult(runBatchId, listingId, platform, result);
    return result;
  }, [reportResult]);

  /**
   * Claim the create, run it through the extension, and tell the server how it went — then record
   * it against this batch. The round trip itself lives in utils/extension-publish so the listing
   * form performs a create exactly the same way, duplicate-post guards included.
   *
   * A create the marketplace held back for now (its pace or daily limit, or a pause: createHeldBack)
   * posted nothing and stays queued, so it is never written down as done — a reload or a retry of the
   * batch sends it. Its caller decides whether to wait and send it again, and shows it otherwise.
   */
  const createOnePlatform = useCallback(async (runBatchId, listingId, platform, payload) => {
    const result = await publishOneExtensionListing(listingId, platform, payload, { surface: 'bulk-cross-list' });
    if (result?.heldBack) return result;
    return publishResult(runBatchId, listingId, platform, result);
  }, [publishResult]);

  const fetchListing = useCallback(async (listingId) => {
    try {
      const response = await axios.get(`${endpoints.listing.details}/${listingId}`);
      return { listing: response?.data, error: null };
    } catch (err) {
      return { listing: null, error: err?.message || 'Voolist couldn’t open this item just now.' };
    }
  }, []);

  const buildPayload = useCallback((listing) => buildExtensionPayload(listing), []);

  // ── Sequential run ────────────────────────────────────────────────────────────────────────

  const runSequentially = useCallback(async (pendingItems, control) => {
    // One entry per (listing, platform) comes in; group it so each listing is fetched once and
    // its platforms run back to back.
    const byListing = new Map();
    pendingItems.forEach(({ listingId, listingTitle, platform }) => {
      if (!byListing.has(listingId)) {
        byListing.set(listingId, { listingId, listingTitle, platforms: [] });
      }
      byListing.get(listingId).platforms.push(platform);
    });

    const totalOps = pendingItems.length;
    let currentOp = 0;

    const advance = (listingTitle, platform) => {
      // The count always moves — it is this run's own position through its own work, not the
      // view's. Only showing it is reserved for the run whose batch is on screen.
      currentOp += 1;
      if (!steersView(control.batchId)) return;

      const progress = {
        current: currentOp,
        total: totalOps,
        currentListing: listingTitle || 'Untitled item',
        currentPlatform: platform,
      };
      setLiveProgress(progress);
      onProgressRef.current?.(progress);
    };

    // Fetch every listing up front so the first paint shows real progress instead of counting
    // up from zero as each fetch lands.
    const fetched = await Promise.all(
      Array.from(byListing.values()).map(async (item) => {
        const { listing, error } = await fetchListing(item.listingId);
        return { ...item, listing, fetchError: error };
      })
    );

    const alreadyDone = readCompletedWork(control.batchId);

    // Decide what still needs doing. Anything skipped here is reported straight away so the
    // progress bar and the results list account for every operation.
    const workItems = [];
    fetched.forEach((entry) => {
      if (entry.fetchError || !entry.listing) {
        entry.platforms.forEach((platform) => {
          publishResult(control.batchId, entry.listingId, platform, {
            success: false,
            error: 'Voolist couldn’t open this item just now.',
            platform,
          });
          advance(entry.listingTitle, platform);
        });
        return;
      }

      const { listing } = entry;
      const platforms = entry.platforms.filter((platform) => {
        const outcome = priorCreateOutcome({
          listing,
          platform,
          fields: PLATFORM_FIELDS[platform],
          previousAttempt: alreadyDone.find(
            (done) => done.listingId === entry.listingId && done.platform === platform
          ),
        });

        if (outcome.kind === 'create') return true;

        // An earlier pass of this same batch already wrote its conclusion down, so showing it
        // again is all that is owed. Everything else is news to this batch's log.
        const record = outcome.kind === 'replay' ? reportResult : publishResult;
        record(control.batchId, entry.listingId, platform, outcome.result);
        advance(entry.listingTitle, platform);
        return false;
      });

      if (platforms.length > 0) {
        workItems.push({ ...entry, platforms });
      }
    });

    const waitWhilePaused = async () => {
      while (control.paused && !control.cancelled) {
        // eslint-disable-next-line no-await-in-loop
        await wait(PAUSE_CHECK_MS);
      }
    };

    // Poshmark keeps refusing once it starts, and nothing about the listing or about Voolist
    // changes its answer, so carrying on spends the seller's closet to learn what the second
    // refusal already said. The marketplace is dropped for the rest of the run; anything else in
    // the run is unaffected, because one marketplace's refusal says nothing about another.
    const refusals = createAutomationRefusalTally();

    const noteAutomationRefusal = (platform) => {
      if (!refusals.record(platform)) return;

      // Kept past the end of the run so the next one can warn before it starts. Production says
      // this is the larger half: the seller whose run was refused went on to start nine more.
      rememberAutomationRefusal(platform);
      trackEvent(ANALYTICS_EVENTS.CROSSLIST_RUN_BLOCKED_AS_AUTOMATION, {
        batchId: control.batchId,
        platform,
        refusals: refusals.refusalsFor(platform),
      });
      if (steersView(control.batchId)) onAutomationBlockRef.current?.(platform);
    };

    // An answer that holds for every item left for a marketplace — Chrome signed in to another account,
    // the marketplace's daily limit reached, or new listings there paused — is given to the rest of that
    // marketplace's items instead of sending them; the other marketplaces carry on.
    const stops = createMarketplaceStops();

    // A marketplace's pace, waited out a quarter-second at a time so a cancel still lands.
    const waitOut = async (ms) => {
      const until = Date.now() + ms;
      while (Date.now() < until && !control.cancelled) {
        // eslint-disable-next-line no-await-in-loop
        await wait(Math.min(PAUSE_CHECK_MS, until - Date.now()));
      }
    };

    // One listing at a time, one platform at a time within it — the extension drives a real
    // browser session and cannot do two creates at once.
    for (let index = 0; index < workItems.length; index += 1) {
      if (control.cancelled) break;
      // eslint-disable-next-line no-await-in-loop
      await waitWhilePaused();
      if (control.cancelled) break;

      const item = workItems[index];
      const payload = buildPayload(item.listing);

      // The marketplaces this listing was sent to, which decide the pause before the next one.
      const attempted = [];

      // eslint-disable-next-line no-restricted-syntax
      for (const platform of item.platforms) {
        if (control.cancelled) break;

        // This marketplace has already refused the run. Its share of this listing is left exactly
        // as it was: nothing is claimed, nothing is reported failed, and the queued create stays
        // recoverable from the listing itself whenever the seller wants to try again.
        // eslint-disable-next-line no-continue
        if (refusals.isRefused(platform)) continue;

        // Not sent, so nothing is claimed and nothing is recorded as tried: the queued create stays
        // as it was, and a reload of the batch sends it once the seller has switched the login or waited.
        const answered = stops.answerFor(platform);
        if (answered) {
          advance(item.listingTitle, platform);
          reportResult(control.batchId, item.listingId, platform, answered);
          // eslint-disable-next-line no-continue
          continue;
        }

        // eslint-disable-next-line no-await-in-loop
        await waitWhilePaused();
        if (control.cancelled) break;

        advance(item.listingTitle, platform);
        // eslint-disable-next-line no-await-in-loop
        let result = await createOnePlatform(control.batchId, item.listingId, platform, payload);

        // Held back for the marketplace's pace between two new listings (another create went up a moment
        // ago): the wait is waited out, and this one sent again, once.
        if (result?.heldBack === 'paceWait' && !control.cancelled) {
          // eslint-disable-next-line no-await-in-loop
          await waitOut(paceWaitMs(result.retryAfter));
          // eslint-disable-next-line no-await-in-loop
          await waitWhilePaused();
          if (!control.cancelled) {
            // eslint-disable-next-line no-await-in-loop
            result = await createOnePlatform(control.batchId, item.listingId, platform, payload);
          }
        }

        if (result?.heldBack) reportResult(control.batchId, item.listingId, platform, result);
        else attempted.push(platform);
        if (isBlockedAsAutomation(result?.error)) noteAutomationRefusal(platform);
        stops.record(platform, result);
      }

      // The pause exists so a run does not read as a script to the marketplace (marketplace-pace). A
      // listing nothing was sent for has nothing to space out, so waiting would only make a stopped run
      // look busy.
      if (index < workItems.length - 1 && !control.cancelled && attempted.length > 0) {
        // eslint-disable-next-line no-await-in-loop
        await wait(pauseAfterListing(attempted));
      }
    }
  }, [buildPayload, createOnePlatform, fetchListing, publishResult, reportResult, steersView]);

  const start = useCallback(async (pendingItems) => {
    // The batch these items belong to, fixed now: the active batch can change while this call is
    // still waiting its turn below, and these items would still belong to the batch that asked.
    const runBatchId = batchIdRef.current;

    // Take the runner over. Whoever holds it may itself be replaced while we wait, and several
    // callers can be waiting on the same run at once, so the holder is re-read after every wait
    // and this only falls through once nothing is running.
    while (controlRef.current && !controlRef.current.finished) {
      const inFlight = controlRef.current;
      // Already working on these items — the caller asked twice.
      if (inFlight.batchId === runBatchId) return;
      inFlight.cancelled = true;
      // eslint-disable-next-line no-await-in-loop
      await inFlight.promise?.catch(() => {});
    }

    if (!Array.isArray(pendingItems) || pendingItems.length === 0) {
      setPhase('done');
      return;
    }

    const control = {
      batchId: runBatchId,
      cancelled: false,
      paused: false,
      finished: false,
      promise: null,
    };
    controlRef.current = control;
    setIsPaused(false);
    setPhase('processing');

    // The run and the promise to wait for it are one thing, so a later caller can never find a
    // run to cancel without also finding the promise that tells it when the run has stopped.
    control.promise = runSequentially(pendingItems, control);

    try {
      await control.promise;
    } catch (err) {
      // A run that throws is still a run that is over — the phase must not stay on 'processing'
      // or the UI waits forever for creates that will never happen.
      console.error('[BulkCrossList] Extension run failed:', err?.message || err);
    } finally {
      control.finished = true;
      if (holdsRunner(control)) {
        setPhase(control.cancelled ? 'idle' : 'done');
      }
    }
  }, [holdsRunner, runSequentially]);

  /**
   * Retry one (listing, platform) on its own. The listing is fetched again first, because the
   * usual reason for a retry is that the seller just fixed the listing in another tab.
   *
   * Deliberately leaves the run's phase and control alone, so a retry can happen alongside an
   * idle main loop without the two fighting over the same state.
   */
  const retry = useCallback(async (listingId, platform) => {
    if (!listingId || !platform) {
      return { success: false, error: 'Missing listing or platform', platform };
    }

    // A retry is always work on the batch the user is looking at.
    const runBatchId = batchIdRef.current;

    const { listing, error } = await fetchListing(listingId);
    if (!listing) {
      return publishResult(runBatchId, listingId, platform, {
        success: false,
        error: error || 'Voolist couldn’t open this item just now.',
        platform,
      });
    }

    const result = await createOnePlatform(runBatchId, listingId, platform, buildPayload(listing));
    if (result?.heldBack) reportResult(runBatchId, listingId, platform, result);
    return result;
  }, [buildPayload, createOnePlatform, fetchListing, publishResult, reportResult]);

  const cancel = useCallback(() => {
    const control = controlRef.current;
    if (control) {
      // A run stopped part-way is a different signal from one that failed — it tells us
      // the seller chose to abandon a cross-list that was already working.
      trackEvent(ANALYTICS_EVENTS.CROSSLIST_RUN_CANCELLED, { batchId: control.batchId });
      control.cancelled = true;
      control.paused = false;
    }
    setIsPaused(false);
  }, []);

  const pause = useCallback(() => {
    const control = controlRef.current;
    if (!control || control.finished) return;
    control.paused = true;
    setIsPaused(true);
    setPhase('paused');
  }, []);

  const resume = useCallback(() => {
    const control = controlRef.current;
    if (!control || !control.paused) return;
    control.paused = false;
    setIsPaused(false);
    setPhase('processing');
  }, []);

  // If the tree goes away mid-run, stop the loop rather than let it keep creating listings for
  // a screen nobody is looking at.
  useEffect(() => () => {
    if (controlRef.current) {
      controlRef.current.cancelled = true;
      controlRef.current.paused = false;
    }
  }, []);

  return { start, retry, cancel, pause, resume, phase, isPaused, liveProgress };
}

export default useExtensionRunner;
